import { useEffect, useState } from "react";
import { supabase } from "@/ajax/clients/supabase";

interface SupabaseSessionEmail {
  email: string | null;
  isLoading: boolean;
}

/**
 * Reads the signed-in user's email straight from the Supabase client
 * session rather than `trpc.user.getUser` (which 401s when signed out). The
 * provider-invite acceptance page renders for both signed-in and signed-out
 * visitors and lives outside the `_provider` authenticated layout, so it
 * can't rely on that layout's loader-fetched user.
 */
export function useSupabaseSessionEmail(): SupabaseSessionEmail {
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) {
        return;
      }
      setEmail(data.session?.user.email ?? null);
      setIsLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setEmail(session?.user.email ?? null);
        setIsLoading(false);
      },
    );

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  return { email, isLoading };
}
